import React from 'react'

type Props = {
    children: React.ReactNode;
    // Changing this (e.g. the active tab id) clears a caught error so
    // switching tabs recovers instead of the fallback sticking forever.
    resetKey: string;
};

type State = {
    error: Error | null;
    resetKey: string;
};

// Monaco (@monaco-editor/react) occasionally throws during its own internal
// effects when many editor instances are mounted/re-rendered together (e.g.
// reordering open tabs re-renders every open editor at once) — a disposal
// race in one editor instance, not a bug in the content it's showing. Left
// uncaught, that crashes the entire workspace. Scoping a boundary around
// just the editor area contains it to "this one tab needs a reload" instead.
class EditorErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null, resetKey: this.props.resetKey };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error };
    }

    static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
        if (props.resetKey !== state.resetKey) {
            return { error: null, resetKey: props.resetKey };
        }
        return null;
    }

    render() {
        if (this.state.error) {
            return (
                <div className="editor-error-fallback">
                    <p>This editor hit a loading error.</p>
                    <button type="button" onClick={() => this.setState({ error: null })}>
                        Try again
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default EditorErrorBoundary
